import { useContext, useEffect, useState } from "react";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { PageContainer } from "../../components/page";
import { PEProgressBar } from "../../components/linear-progress";
import {
  Box,
  Container,
  Fade,
  Skeleton,
  Stack,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getKioskAllHealth } from "../../api/new-api";
import KioskHealthContainer from "./KioskHealthContainer";
import { runAuthHandler } from "../util";
import { useNavigate } from "react-router";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react";

const fetchData = async ({
  setData,
  setLoading,
  setError,
}: {
  setData: any;
  setLoading: any;
  setError: any;
}) => {
  setError("");
  setLoading(true);
  try {
    const res = await getKioskAllHealth();
    if (res.data) {
      setData(res.data);
    } else {
      setError("Error getting kiosk health data ");
    }
  } catch (error) {
    setError("Error getting  kiosk health data");
  } finally {
    setLoading(false);
  }
};

const PAGE_TITLE = "Kiosk Attractor App Health Status";

const KioskHealthPage = () => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>("");
  const [data, setData] = useState<any>([]);

  const handleVenue = () => {
    if (selectedVenue?.venueId !== 17) {
      navigate("/");
    }
  };

  useEffect(() => {
    runAuthHandler({ userStore, navigate });
    handleVenue();
  }, [selectedVenue]);

  useEffect(() => {
    // Fetch data every 8 seconds
    fetchData({ setData, setLoading, setError });
    const interval = setInterval(() => {
      fetchData({ setData, setLoading, setError });
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <PageContainer>
      <BreadCrumbs
        routes={[
          { path: "/home", breadcrumbName: "Home" },
          { path: "/kiosk-health", breadcrumbName: "Kiosk Attractor Health" },
        ]}
      />
      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {PAGE_TITLE}
          </Typography>
          <IconButton
            aria-label="refresh"
            onClick={() => fetchData({ setData, setLoading, setError })}
            size="large"
          >
            <RefreshIcon />
          </IconButton>
        </Stack>

        {loading && data?.length === 0 && (
          <Stack spacing={2}>
            <PEProgressBar />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={160} />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        )}

        {error && data?.length === 0 && <Alert severity="error">{error}</Alert>}

        {data?.length > 0 && (
          <Fade in={true} timeout={400}>
            <Box>
              {/* Keep showing content during refetch cycles */}
              <KioskHealthContainer data={data} />
            </Box>
          </Fade>
        )}

        {!loading && !error && data?.length === 0 && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            No kiosk health found
          </Typography>
        )}
      </Container>
    </PageContainer>
  );
};

export default observer(KioskHealthPage);
